/**
 * ClientAvatar (KAN-857, KAN-864)
 *
 * Round Telegram avatar of the client: the picture from `avatarUrl`
 * (/api/v1/clients/<id>/avatar/?v=..., needs the token, so it is loaded through
 * useApiFileSrc), otherwise initials of the client name. The `v` param of the
 * url changes with the picture, so the url itself is the cache version.
 * KAN-864: a loaded picture is a button that opens it large (ImageLightbox);
 * the click does not reach the row/card around. Initials are not clickable.
 */
import { useState, type MouseEvent } from 'react'
import { ImageLightbox } from '@/components/common/ImageLightbox'
import { RoundAvatar, type RoundAvatarSize } from '@/components/common/RoundAvatar'
import { useApiFileSrc } from '@/hooks/useApiFileSrc'

export const CLIENT_AVATAR_OPEN_LABEL = 'Открыть фото клиента'

export interface ClientAvatarProps {
  /** avatar_url from the backend; "" or absent: no picture. */
  avatarUrl?: string | null
  /** Client display name for the initials. */
  name?: string
  /** Diameter in px. */
  size?: RoundAvatarSize
  className?: string
}

export function ClientAvatar({ avatarUrl, name = '', size = 32, className }: ClientAvatarProps) {
  const url = avatarUrl ?? ''
  const src = useApiFileSrc(url, url)
  const [failedSrc, setFailedSrc] = useState<string | null>(null)
  const [open, setOpen] = useState(false)
  const picture = src && failedSrc !== src ? src : null

  if (!picture) {
    return <RoundAvatar src={null} name={name} size={size} className={className} testId="client-avatar" />
  }

  const handleOpen = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    event.stopPropagation()
    setOpen(true)
  }

  return (
    <>
      <button
        type="button"
        aria-label={CLIENT_AVATAR_OPEN_LABEL}
        onClick={handleOpen}
        className="shrink-0 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
      >
        <RoundAvatar
          src={picture}
          name={name}
          size={size}
          className={className}
          testId="client-avatar"
          onError={setFailedSrc}
        />
      </button>
      <ImageLightbox
        src={picture}
        open={open}
        onOpenChange={setOpen}
        title={name || 'Фото клиента'}
        imageTestId="client-avatar-large"
        backdropTestId="client-avatar-backdrop"
      />
    </>
  )
}
